import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Elements } from '@stripe/react-stripe-js'
import useCart from '@/public/hooks/use-cart'
import { setCheckoutFormReset } from '@/public/redux/cartSlice'

const APPEARANCE = {
  theme: 'stripe',
  variables: {
    fontFamily: 'GT America, sans-serif',
    fontSizeBase: '17.5px',
    fontLineHeight: '24px',
    fontWeightNormal: 400,
  },
  rules: {
    '.Input': {
      border: '2px solid #7b796b',
    },
    '.Input:focus': {
      border: '2px solid #4884e0',
      boxShadow: 'none',
    },
  },
}

// Which Elements mode a cart needs, and why it is not simply "whatever the cart
// looks like right now".
//
// 'subscription' has Stripe.js pre-create a PaymentMethod and confirm with a
// plain payment_method id, making no automatic-payment-methods claim, so it
// confirms EITHER intent shape. 'payment' sends client_context asserting
// automatic payment methods, and is rejected against the subscription invoice's
// intent. So gate on membership PRESENCE: any cart that could end up
// membership-only at submit gets the mode that works either way.
//
// Not applied to every cart, because mode also constrains which methods
// Elements offers -- forcing 'subscription' on pure-product carts would drop the
// BNPL options their intents support.
export function elementsOptionsForCart(cart) {
  const amount = Math.round((parseFloat(cart?.calculated_total_after_savings) || 0) * 100)

  let mode
  if (amount > 0) {
    mode = cart?.contains_a_membership ? 'subscription' : 'payment'
  } else {
    mode = 'setup'
  }

  const options = { mode, currency: 'usd', appearance: APPEARANCE }
  if (mode !== 'setup') {
    options.amount = amount
  }
  if (cart?.contains_subscriptions) {
    options.setupFutureUsage = 'off_session'
  }

  return options
}

// SS-2918. Stripe.js fixes `mode` when the Elements instance is created and
// offers no way to change it -- elements.update() takes `amount` and `currency`,
// never `mode`. Deriving it once from the render-time cart therefore freezes a
// value that has to match SUBMIT-time state, and any cart change that crosses
// the membership boundary afterwards desyncs Elements from the PaymentIntent the
// server will mint, silently, until Place Order fails.
//
// Two halves close that:
//
//   1. The options come from the LIVE cart (useCart), not the render-time prop,
//      so the boundary is evaluated continuously rather than once.
//   2. `key={mode}` makes React discard and rebuild the provider when the
//      boundary actually flips -- the only way to move a value Stripe will not
//      let us update.
//
// Keyed on `mode` ALONE, deliberately. Keying on the whole options object would
// rebuild on every quantity change; `amount` and `currency` are exactly what
// react-stripe-js already pushes through elements.update() without a rebuild.
export default function CheckoutElements({ stripePromise, renderTimeCart, children }) {
  const dispatch = useDispatch()
  const { cart } = useCart()

  // The render-time cart is the fallback, not the source. OrdersController#new
  // re-syncs before rendering, so it is a good first paint -- it just stops
  // being authoritative the moment the live cart resolves.
  const liveCart = cart || renderTimeCart
  const options = useMemo(() => elementsOptionsForCart(liveCart), [liveCart])
  const { mode } = options

  // The baseline is the first LIVE mode, not the first rendered one. A
  // render-time fallback that disagrees with the first fetch rebuilds Elements
  // too, but that happens before anyone has typed anything, so reporting it
  // would be noise rather than news.
  const baselineMode = useRef(null)

  useEffect(() => {
    if (!cart) { return }

    if (baselineMode.current === null) {
      baselineMode.current = mode
      return
    }

    if (baselineMode.current === mode) { return }

    baselineMode.current = mode
    // Announced on every post-baseline flip, including one the customer had not
    // yet typed into. The rebuild is what clears the form, and this component
    // sits ABOVE the form precisely so it survives that -- which also means it
    // cannot see whether anything was lost. Telling someone their form reset
    // when it was already empty is mild noise; letting them find it blank with
    // no explanation is the failure this is here to prevent.
    dispatch(setCheckoutFormReset(true))
  }, [cart, mode, dispatch])

  return (
    <Elements key={mode} stripe={stripePromise} options={options}>
      {children}
    </Elements>
  )
}
